import {
  PARTICIPATION_ACTION_LABELS,
  PARTICIPATION_NOTE_MAX,
  type ParticipationRouteAction,
  participationReassignSchema,
  participationTransitionSchema,
} from '@fruuit/shared'
import { Button, Callout, Dialog, DialogCancel, Select, Textarea, useToast } from '@fruuit/ui'
import { type FormEvent, useState } from 'react'
import { api } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { type FieldErrors, serverErrors, validate } from '../engagements/forms'
import { LANE_D_KEYS } from '../engagements/queries'

export type TransitionAction = ParticipationRouteAction | 'reassign'

const COPY: Record<TransitionAction, { note: string; hint: string; done: string }> = {
  acknowledge: {
    note: 'Response to the investor',
    hint: 'Say what happens next. An acknowledgment is not a booking.',
    done: 'Request acknowledged.',
  },
  'consent-pending': {
    note: 'Response to the investor',
    hint: 'Explain that you are asking the company whether an investor may attend.',
    done: 'Marked as awaiting company consent.',
  },
  confirm: {
    note: 'Confirmation note',
    hint: 'Confirm the arrangement agreed with the company. Nothing is sent externally.',
    done: 'Participation confirmed.',
  },
  decline: {
    note: 'Reason for declining',
    hint: 'The investor sees this note. Suggest an alternative where you can.',
    done: 'Request declined.',
  },
  withdraw: {
    note: 'Note (optional)',
    hint: 'Earlier manager responses stay on the record.',
    done: 'Request withdrawn.',
  },
  reassign: {
    note: 'Reason for reassigning',
    hint: 'Recorded in the request history and the audit trail.',
    done: 'Request reassigned.',
  },
}

export interface TransitionDialogProps {
  open: boolean
  onClose: () => void
  requestId: string
  revision: number
  action: TransitionAction
  /** Reassignment targets (staff only). */
  managers?: { id: string; name: string }[]
  currentManagerId?: string | null
}

/**
 * Note dialog for a participation transition (P-PART-001/-002). The server re-checks the case
 * and meeting with its own clock, and refuses stale revisions (409), so the dialog shows the
 * server's message on failure instead of guessing.
 */
export function TransitionDialog({
  open,
  onClose,
  requestId,
  revision,
  action,
  managers = [],
  currentManagerId,
}: TransitionDialogProps) {
  const copy = COPY[action]
  const choices = managers.filter((m) => m.id !== currentManagerId)
  const [note, setNote] = useState('')
  const [managerId, setManagerId] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const { toast } = useToast()

  function close() {
    setErrors({})
    onClose()
  }

  const mutation = useApiMutation(
    (input: Record<string, unknown>, { requestId: rid }) =>
      api.post(`/participation-requests/${requestId}/${action}`, input, { requestId: rid }),
    {
      invalidate: LANE_D_KEYS,
      onSuccess: () => {
        setNote('')
        close()
        toast({ tone: 'success', message: copy.done })
      },
      onError: (error) => setErrors(serverErrors(error).fields),
    },
  )
  const formError = mutation.error ? serverErrors(mutation.error).message : null

  function submit(event: FormEvent) {
    event.preventDefault()
    const checked =
      action === 'reassign'
        ? validate(participationReassignSchema, {
            revision,
            manager_id: managerId || choices[0]?.id || '',
            note,
          })
        : validate(participationTransitionSchema, { revision, note: note.trim() || null })
    if (!checked.ok) {
      setErrors(checked.errors)
      return
    }
    const needsNote = action !== 'withdraw' && note.trim().length < 8
    if (needsNote) {
      setErrors({ note: 'Write at least 8 characters for the record.' })
      return
    }
    setErrors({})
    mutation.mutate(checked.data as Record<string, unknown>)
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      size="sm"
      title={PARTICIPATION_ACTION_LABELS[action]}
      footer={
        <>
          <DialogCancel />
          <Button
            type="submit"
            form="participation-transition-form"
            variant={action === 'decline' || action === 'withdraw' ? 'danger' : 'primary'}
            loading={mutation.isPending}
          >
            {PARTICIPATION_ACTION_LABELS[action]}
          </Button>
        </>
      }
    >
      <form id="participation-transition-form" onSubmit={submit} noValidate className="space-y-4">
        {action === 'reassign' ? (
          <Select
            label="New manager"
            required
            options={choices.map((m) => ({ value: m.id, label: m.name }))}
            value={managerId || choices[0]?.id || ''}
            onChange={(event) => setManagerId(event.target.value)}
            error={errors.manager_id}
          />
        ) : null}
        <Textarea
          label={copy.note}
          hint={copy.hint}
          required={action !== 'withdraw'}
          rows={4}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={PARTICIPATION_NOTE_MAX}
          error={errors.note}
        />
        {formError ? (
          <Callout tone="danger" role="alert">
            {formError}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  )
}
